/**
 * The one nav table. Every rendering derives from this — top nav, More panel, bottom nav, More sheet.
 *
 * The design has THREE competing versions of this list and they disagree:
 *   A. the desktop More panel — Records / Watch & plan / Reference, with Garage absent entirely (reachable
 *      only via the brand link, on a multi-vehicle app);
 *   B. dashboard.dc.html's mobile sheet — Daily / Records / Watch & plan / Reference, Garage first;
 *   C. the other 15 screens' mobile sheets — a flat 17-link grid, no headings.
 *
 * C is B's exact order with the headings deleted, which is strong evidence B is the intended design and C the
 * un-migrated older one. **Owner's call (2026-07-15): B is canonical.** Desktop keeps its six top-level links;
 * the More panel adopts B's groups.
 *
 * `label` / `nav` / `bottom` are not drift — they are three REGISTERS of one screen. "Fuel log" names it in a
 * list, "Fuel" fits a nav bar, "Home" is what the dashboard is called from a phone's bottom bar. Each
 * rendering picks its column.
 */

export type ScreenId =
  | 'garage'
  | 'dashboard'
  | 'fuel'
  | 'checks'
  | 'expenses'
  | 'mileage'
  | 'wash'
  | 'service'
  | 'tasks'
  | 'tyres'
  | 'documents'
  | 'issues'
  | 'budget'
  | 'equipment'
  | 'data-integrity'
  | 'vehicle-info'

/**
 * Where the app can be, which is two more places than the nav table lists.
 *
 * The assistant is a screen below 900 px and a docked panel above it, so it has a route and no nav entry —
 * putting it in {@link ScreenId} would add an item to every menu, the More sheet and the bottom bar for
 * something that is reached from a button in the bar itself. `current` therefore takes this, and every
 * comparison against a nav id simply never matches while the assistant is open, which is the correct
 * highlight: none.
 *
 * **The account screen is the same shape and joined for the same reason** (2026-08-15). It is reached from one
 * control - the identity menu in the bar - and it is the only screen in the app that is about the person
 * rather than a car, so filing it under a vehicle group would be wrong twice over. Note the consequence: it is
 * not in {@link SCREENS}, so `hrefFor` cannot build its URL and `UserMenu` names the path directly, exactly as
 * `NavMoreSheet` does for the assistant.
 *
 * **The admin screen is the third, and joined for a third variation on the same reason** (2026-08-22,
 * DEC-023). It is reached from the identity menu, it is about the deployment rather than about a car, and it
 * is visible only to a principal holding `admin:read` - so a nav entry would either be dead for almost
 * everybody or would have to be conditionally removed from a table whose whole value is being static.
 */
export type CurrentScreen = ScreenId | 'assistant' | 'account' | 'admin'

export type NavGroup = 'daily' | 'records' | 'watch' | 'reference'

export const GROUP_LABELS: Record<NavGroup, string> = {
  daily: 'Daily',
  records: 'Records',
  watch: 'Watch & plan',
  reference: 'Reference',
}

/** Rendering order for the groups. */
export const GROUP_ORDER: readonly NavGroup[] = ['daily', 'records', 'watch', 'reference']

export interface ScreenDef {
  /** In a list or a sheet — the full name. */
  label: string
  /** In the desktop top nav, where space is tight. */
  nav?: string
  /** In the bottom nav's fixed slots. */
  bottom?: string
  group: NavGroup
  /** Present in the desktop top bar rather than behind More. */
  topLevel: boolean
  /**
   * False only for the garage.
   *
   * This is why the garage's nav is structurally different rather than accidentally so: it is the screen you
   * are on *before* choosing a vehicle, so it cannot render vehicle-scoped links. There is no vehicle to
   * scope them to.
   */
  scoped: boolean
}

/**
 * `Record<ScreenId, ScreenDef>` on purpose: add a screen to the union and this fails to compile until it is
 * filed under a group. A screen cannot be reachable-but-unfiled, which is how Garage went missing from the
 * design's desktop menu.
 */
export const SCREENS: Record<ScreenId, ScreenDef> = {
  garage: { label: 'Garage', group: 'daily', topLevel: false, scoped: false },
  dashboard: { label: 'Dashboard', nav: 'Dashboard', bottom: 'Home', group: 'daily', topLevel: true, scoped: true },
  fuel: { label: 'Fuel log', nav: 'Fuel', bottom: 'Fuel', group: 'daily', topLevel: true, scoped: true },
  checks: { label: 'Regular checks', nav: 'Checks', bottom: 'Checks', group: 'daily', topLevel: true, scoped: true },
  expenses: { label: 'Expenses', nav: 'Expenses', group: 'daily', topLevel: true, scoped: true },
  mileage: { label: 'Mileage readings', group: 'daily', topLevel: false, scoped: true },
  wash: { label: 'Wash log', group: 'daily', topLevel: false, scoped: true },
  service: { label: 'Service history', nav: 'Service', group: 'records', topLevel: true, scoped: true },
  tasks: { label: 'Tasks', nav: 'Tasks', group: 'records', topLevel: true, scoped: true },
  tyres: { label: 'Tyre log', group: 'records', topLevel: false, scoped: true },
  documents: { label: 'Documents', group: 'records', topLevel: false, scoped: true },
  issues: { label: 'Issues watchlist', group: 'watch', topLevel: false, scoped: true },
  budget: { label: 'Budget', group: 'watch', topLevel: false, scoped: true },
  equipment: { label: 'Equipment', group: 'watch', topLevel: false, scoped: true },
  // The two screens whose bottom-nav centre slot is a link, not a FAB - they have no *single* primary write
  // action. Vehicle info has eight editors, which is the same thing from the other direction: no one of them
  // is the action the screen is for.
  'data-integrity': { label: 'Data integrity', bottom: 'Flags', group: 'watch', topLevel: false, scoped: true },
  'vehicle-info': { label: 'Vehicle info', bottom: 'Ref', group: 'reference', topLevel: false, scoped: true },
}

export const SCREEN_IDS = Object.keys(SCREENS) as ScreenId[]

/** The six that sit directly in the desktop top bar. */
export const TOP_LEVEL: readonly ScreenId[] = SCREEN_IDS.filter((id) => SCREENS[id].topLevel)

/**
 * Grouped, for the More panel and the More sheet.
 *
 * `excludeTopLevel` is the difference between the two: the desktop panel holds what the top bar does not
 * already show, while the mobile sheet is the *only* menu there is, so it lists everything.
 */
export function groupedScreens(options: { excludeTopLevel: boolean }): Array<{ group: NavGroup; ids: ScreenId[] }> {
  return GROUP_ORDER.map((group) => ({
    group,
    ids: SCREEN_IDS.filter(
      (id) => SCREENS[id].group === group && !(options.excludeTopLevel && SCREENS[id].topLevel),
    ),
  })).filter((g) => g.ids.length > 0)
}
